import { acceptHMRUpdate, defineStore } from "pinia";
import { getContexts, ClientContext, ProgramContext, CurrentContextUser } from "@/apis/contexts.api";
import { setHTTPHeader } from "@/services/http.service";

interface IState {
  clients: ClientContext[];
  selectedProgram: ProgramContext | null;
  currentUser: CurrentContextUser | null;
  loading: boolean;
}

const storedProgramId = () => localStorage.getItem("selectedProgramId");

const applyShellTheme = (program: ProgramContext | null) => {
  const root = document.documentElement;

  root.style.setProperty("--caravel-primary", program?.primaryColor || "#009C9B");
  root.style.setProperty("--caravel-accent", program?.accentColor || "#009C9B");
  root.style.setProperty("--caravel-nav", program?.navColor || "#08263A");
};

export const useContextStore = defineStore("context.store", {
  state: (): IState => ({
    clients: [],
    selectedProgram: null,
    currentUser: null,
    loading: false,
  }),
  getters: {
    programs: (state): ProgramContext[] => state.clients.flatMap((client) => client.programs),
    isDemo: (state): boolean => !!state.selectedProgram?.demo,
    stediDemoMode: (state): boolean => !!state.selectedProgram?.stediDemoMode,
    currentUserRole: (state): string | null => state.currentUser?.role || null,
    isCaravelAdmin: (state): boolean => state.currentUser?.role === "caravel_admin",
    isAdminUser: (state): boolean =>
      state.currentUser?.role === "caravel_admin" || state.currentUser?.role === "operator_admin",
    enabledModuleKeys: (state): string[] =>
      state.selectedProgram?.enabledModules?.map((module) => module.key) || [],
    moduleEnabled: (state) => (key: string): boolean =>
      !!state.selectedProgram?.enabledModules?.some((module) => module.key === key),
  },
  actions: {
    async loadContexts() {
      this.loading = true;

      try {
        const response = await getContexts();
        this.clients = response.contexts;
        this.currentUser = response.currentUser;

        const programs = this.clients.flatMap((client) => client.programs);
        const savedProgramId = storedProgramId();
        const selected = programs.find((program) => String(program.id) === savedProgramId)
          || programs.find((program) => program.id === response.currentProgramId)
          || programs[0]
          || null;

        if (selected) {
          this.setSelectedProgram(selected, false);
        }
      } finally {
        this.loading = false;
      }
    },
    setSelectedProgram(program: ProgramContext, persist = true) {
      this.selectedProgram = program;
      applyShellTheme(program);
      localStorage.setItem("selectedProgramId", String(program.id));
      setHTTPHeader({ "X-Program-ID": String(program.id) });

      if (persist) {
        window.location.reload();
      }
    },
  },
});

if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useContextStore, import.meta.hot));
}
